import { Injectable, effect, signal, computed, inject } from '@angular/core';
import { Subject, firstValueFrom, timeout } from 'rxjs';
import { TemaService } from '../../services/tema.service';
import { TEMA_POR_DEFECTO, TemaSitio, aplicarTemaSitio, normalizarTema } from '../theme/site-theme';

export interface layoutConfig {
  preset?: string;
  primary?: string;
  surface?: string | undefined | null;
  darkTheme?: boolean;
  menuMode?: string;
}

interface LayoutState {
  staticMenuDesktopInactive?: boolean;
  overlayMenuActive?: boolean;
  configSidebarVisible?: boolean;
  staticMenuMobileActive?: boolean;
  menuHoverActive?: boolean;
}

interface MenuChangeEvent {
  key: string;
  routeEvent?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class LayoutService {
  _config: layoutConfig = {
    preset: 'Spa',
    primary: 'rose',
    surface: 'viva',
    darkTheme: false,
    menuMode: 'overlay'
  };

  _state: LayoutState = {
    staticMenuDesktopInactive: false,
    overlayMenuActive: false,
    configSidebarVisible: false,
    staticMenuMobileActive: false,
    menuHoverActive: false
  };

  layoutConfig = signal<layoutConfig>(this._config);

  layoutState = signal<LayoutState>(this._state);

  private configUpdate = new Subject<layoutConfig>();

  private overlayOpen = new Subject<any>();

  private menuSource = new Subject<MenuChangeEvent>();

  private resetSource = new Subject();

  menuSource$ = this.menuSource.asObservable();

  resetSource$ = this.resetSource.asObservable();

  configUpdate$ = this.configUpdate.asObservable();

  overlayOpen$ = this.overlayOpen.asObservable();

  theme = computed(() => (this.layoutConfig()?.darkTheme ? 'light' : 'dark'));

  isSidebarActive = computed(
    () => this.layoutState().overlayMenuActive || this.layoutState().staticMenuMobileActive
  );

  isDarkTheme = computed(() => this.layoutConfig().darkTheme);

  getPrimary = computed(() => this.layoutConfig().primary);

  getSurface = computed(() => this.layoutConfig().surface);

  isOverlay = computed(() => this.layoutConfig().menuMode === 'overlay');

  transitionComplete = signal<boolean>(false);

  private initialized = false;

  constructor() {
    effect(() => {
      const config = this.layoutConfig();
      if (config) {
        this.onConfigUpdate();
      }
    });

    effect(() => {
      const config = this.layoutConfig();

      if (!this.initialized || !config) {
        this.initialized = true;
        return;
      }

      this.handleDarkModeTransition(config);
    });


    // La paleta pública la decide el dueño y vive en el servidor: aquí solo se recuerda el estado del menú.
    effect(() => {
      localStorage.setItem(
        'staticMenuDesktopInactive', 
        this.layoutState().staticMenuDesktopInactive 
          ? 'true' 
          : 'false'
      );
    });

    let staticMenuDesktopInactive = 
      localStorage.getItem('staticMenuDesktopInactive') === 'true' ? true : false;

    this.layoutState.update((prev) => ({
      ...prev,
      staticMenuDesktopInactive: staticMenuDesktopInactive
    }));
  }

  private temaService = inject(TemaService);

  /** Última paleta confirmada por el servidor (base para descartar cambios en el editor del dueño). */
  temaGuardado = signal<TemaSitio>(TEMA_POR_DEFECTO);

  /**
   * Carga la paleta del servidor y la aplica antes de pintar la web.
   * Si la API tarda o falla, se usa la paleta por defecto para no bloquear la carga.
   */
  async cargarTemaSitio(): Promise<void> {
    let tema = TEMA_POR_DEFECTO;
    try {
      const respuesta = await firstValueFrom(this.temaService.get().pipe(timeout(2500)));
      if (respuesta?.code === 200) tema = normalizarTema(respuesta.data);
    } catch {
      tema = TEMA_POR_DEFECTO;
    }
    this.aplicarTema(tema, true);
  }

  /** Aplica un tema a la interfaz; con `confirmado` lo marca como el guardado en servidor. */
  aplicarTema(tema: TemaSitio, confirmado = false): void {
    if (confirmado) this.temaGuardado.set(tema);
    this.layoutConfig.update((state) => ({
      ...state,
      primary: tema.primary_color,
      surface: tema.surface,
      preset: tema.preset,
      darkTheme: tema.dark_mode
    }));
    aplicarTemaSitio(tema);
    this.toggleDarkMode(this.layoutConfig());
  }

  /** Tema actualmente aplicado, con el formato del servidor. */
  temaActual(): TemaSitio {
    const c = this.layoutConfig();
    return {
      primary_color: c.primary ?? TEMA_POR_DEFECTO.primary_color,
      surface: c.surface ?? TEMA_POR_DEFECTO.surface,
      preset: c.preset ?? TEMA_POR_DEFECTO.preset,
      dark_mode: !!c.darkTheme
    };
  }

  private handleDarkModeTransition(config: layoutConfig): void {
    if ((document as any).startViewTransition) {
      this.startViewTransition(config);
    } else {
      this.toggleDarkMode(config);
      this.onTransitionEnd();
    }
  }

  private startViewTransition(config: layoutConfig): void {
    const transition = (document as any).startViewTransition(() => {
      this.toggleDarkMode(config);
    });

    transition.ready
      .then(() => {
        this.onTransitionEnd();
      })
      .catch(() => {});
  }

  toggleDarkMode(config?: layoutConfig): void {
    const _config = config || this.layoutConfig();
    if (_config.darkTheme) {
      document.documentElement.classList.add('app-dark');
    } else {
      document.documentElement.classList.remove('app-dark');
    }
  }

  private onTransitionEnd() {
    this.transitionComplete.set(true);
    setTimeout(() => {
      this.transitionComplete.set(false);
    });
  }

  onMenuToggle() {
    if (this.isOverlay()) {
      this.layoutState.update((prev) => ({ ...prev, overlayMenuActive: !this.layoutState().overlayMenuActive }));

      if (this.layoutState().overlayMenuActive) {
        this.overlayOpen.next(null);
      }
    }

    if (this.isDesktop()) {
      this.layoutState.update((prev) => ({
        ...prev,
        staticMenuDesktopInactive: !this.layoutState().staticMenuDesktopInactive
      }));
    } else {
      this.layoutState.update((prev) => ({
        ...prev,
        staticMenuMobileActive: !this.layoutState().staticMenuMobileActive
      }));

      if (this.layoutState().staticMenuMobileActive) {
        this.overlayOpen.next(null);
      }
    }
  }

  isDesktop() {
    return window.innerWidth > 991;
  }

  isMobile() {
    return !this.isDesktop();
  }

  onConfigUpdate() {
    this._config = { ...this.layoutConfig() };
    this.configUpdate.next(this.layoutConfig());
  }

  onMenuStateChange(event: MenuChangeEvent) {
    this.menuSource.next(event);
  }

  reset() {
    this.resetSource.next(true);
  }
}
